"use client";

import { Button, Stack, StackProps, Typography, TypographyProps } from "@mui/material";
import { CSSProperties, FC, Fragment } from "react";
import SalifySvgIconString from "../icon/svg/svg-icon-string";
import SalifyTypography from "../typography/default";
import { SalifyImage } from "../ui/image/image";
import { SalifyLinkV2 } from "../ui/link/link";
import SalifyComponent from "../salify-component/default";
import { toAssetImages } from "src/lib/utils";

interface SalifyFeatureCardProps {
  direction?: "row" | "column" | undefined;
  link: any;
  img?: {
    type: "svg" | undefined;
    src: string;
    width: number | string;
    height: number | string;
    style?: CSSProperties | undefined;
  };
  content?: {
    subTitle?: {
      text: string | undefined;
      cssProps?: TypographyProps;
    };
    title?: {
      text: string | undefined;
      cssProps?: TypographyProps;
    };
    description?: {
      text: string | undefined;
      cssProps?: TypographyProps;
    };
    stackProps?: StackProps;
  };
  stackProps?: StackProps;
  bottomComponents?: any[];
}

const SalifyFeatureCard: FC<SalifyFeatureCardProps> = ({
  direction = "column",
  link,
  img,
  content,
  stackProps,
  bottomComponents,
}) => {
  const visibleText =
    content?.title?.text || content?.subTitle?.text || content?.description?.text || "";
  const linkAriaLabel = link && !visibleText.trim() ? "Xem chi tiết" : undefined;

  const linkedBody = (
    <Stack
      {...stackProps}
      direction={direction}
      alignItems={stackProps?.alignItems || "center"}
      spacing={stackProps?.spacing ?? 2}
    >
      {img && img.type === "svg" ? (
        (() => {
          const s = (img.src || "").trim();
          if (!s) return null;
          if (s.startsWith("<")) {
            return <SalifySvgIconString svgString={s} fontSize={img.width || img.height || 40} />;
          }
          return (
            <SalifyImage
              src={toAssetImages(s)}
              alt={visibleText.trim() || ""}
              style={{ height: img.height, width: img.width, ...img?.style }}
            />
          );
        })()
      ) : (
        <SalifyImage
          src={img?.src ? toAssetImages(typeof img.src === "string" ? img.src : "") : undefined}
          alt={visibleText.trim() || undefined}
          style={{ height: img?.height, width: img?.width, ...img?.style }}
        />
      )}

      {content && (
        <Stack
          alignItems={
            direction === "column"
              ? content?.stackProps?.alignItems || "center"
              : content?.stackProps?.alignItems
          }
          {...content?.stackProps}
        >
          {content.subTitle?.text && (
            <SalifyTypography text={content.subTitle.text} cssProps={content.subTitle.cssProps} />
          )}
          {content.title?.text && (
            <SalifyTypography text={content.title.text} cssProps={content.title.cssProps} />
          )}
          {content.description?.text && (
            <SalifyTypography
              text={content.description.text}
              cssProps={content.description.cssProps}
            />
          )}
        </Stack>
      )}
    </Stack>
  );

  return (
    <Stack
      height={stackProps?.height ?? "100%"}
      justifyContent={stackProps?.justifyContent || "space-between"}
      spacing={stackProps?.spacing ?? 2}
    >
      {link ? (
        <SalifyLinkV2 href={link} aria-label={linkAriaLabel} sx={{ color: "inherit" }}>
          {linkedBody}
        </SalifyLinkV2>
      ) : (
        linkedBody
      )}

      {!!bottomComponents?.length && <SalifyComponent components={bottomComponents} />}
    </Stack>
  );
};

SalifyFeatureCard.displayName = "SalifyFeatureCard";

export default SalifyFeatureCard;
